import {useContext, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction} from 'react';
import {NetworkContext} from '../../lib/context';
import {useParams} from 'react-router-dom';
import {networkOptimization} from '../../../../lib/api';
import {streamResponseToDownload} from '../../../../lib/download';
import {type IExportProps} from './components/Export';
import {IFetchReportsPayload} from '../../lib/types';

export enum ReportsPageTab {
    Roster,
    NetworkAdequacy
}

const tabs = [
    {
        label: 'Roster'
    },
    {
        label: 'Network Adequacy'
    }
];

export type ViewType = {
    name: string;
    text: string;
    index: number;
};

const RosterTabViewTypes: ViewType[] = [
    {name: 'Provider', text: 'Provider Roster', index: 0},
    {name: 'Facility', text: 'Facility Roster', index: 1},
    {name: '', text: 'Combined Roster', index: 2}
];

export enum NetworkAdequacyTabViewEnum {
    Providers,
    AvailableProviders,
    AdequacyScores
}

const NetworkAdequacyTabViewTypes: ViewType[] = [
    {name: '', text: 'Providers', index: 0},
    {name: '', text: 'Available Providers', index: 1},
    {name: '', text: 'Adequacy Scores', index: 2}
];

const DOWNLOAD_PROVIDERS_REPORT_ITEM = 'providersReport';
const DOWNLOAD_AVAILABLE_PROVIDERS_REPORT_ITEM = 'availableProvidersReport';
const DOWNLOAD_ADEQUACY_SCORES_REPORT_ITEM = 'adequacyScoresReport';
const DOWNLOAD_STANDARD_REPORTING_ITEM = 'standardReporting';

const createRosterMenuItem = (
    label: string,
    exportingState: {type: string, format: 'csv' | 'tab'} | null,
    setExportingState: Dispatch<SetStateAction<{type: string, format: 'csv' | 'tab'} | null>>,
    standardType: string
): IExportProps['menuItems'][0] => {
    const disabled = Boolean(exportingState);
    const standardTypeLower = standardType.toLowerCase();

    // For QHP, create nested menu with format options
    if (standardType.toLowerCase() === 'qhp') {
        return {
            disabled,
            label,
            submenuItems: [
                {
                    label: 'Tab-delimited (.txt)',
                    onClick: () => setExportingState({type: standardTypeLower, format: 'tab'}),
                    disabled
                },
                {
                    label: 'CSV (.csv)',
                    onClick: () => setExportingState({type: standardTypeLower, format: 'csv'}),
                    disabled
                }
            ]
        };
    }

    // For MA and other types, create flat menu item
    return {
        disabled,
        label,
        onClick: () => setExportingState({type: standardTypeLower, format: 'tab'})
    };
};

const useReportsPageController = () => {

    const [exportingProviderRoster, setExportingProviderRoster] = useState<{type: string, format: 'csv' | 'tab'} | null>(null);
    const [exportingFacilityRoster, setExportingFacilityRoster] = useState<{type: string, format: 'csv' | 'tab'} | null>(null);
    const [exportingCombinedRoster, setExportingCombinedRoster] = useState<{type: string, format: 'csv' | 'tab'} | null>(null);
    const [activeTab, setActiveTab] = useState<ReportsPageTab>(ReportsPageTab.Roster);
    const [selectedViewType, setSelectedViewType] = useState<ViewType>(RosterTabViewTypes[0]);

    const networkOptimizationStore = useContext(NetworkContext);
    const {rosterId, revisionId} = useParams();

    useEffect(() => {
        if (!exportingProviderRoster) return;

        const abortController = new AbortController();
        const format = exportingProviderRoster.format === 'csv' ? 'csv' : 'txt';
        networkOptimization
            .downloadProviderRoster(
                networkOptimizationStore.runId!,
                networkOptimizationStore.revisionId!,
                exportingProviderRoster.type,
                format,
                abortController.signal
            )
            .then(async (response) => {
                await streamResponseToDownload(response, `provider-roster-${exportingProviderRoster.type}.zip`);
                setExportingProviderRoster(null);
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    networkOptimizationStore.handleApiError(err, 'Download Failed');
                }
                setExportingProviderRoster(null);
            });

        return () => abortController.abort();
    }, [exportingProviderRoster, networkOptimizationStore]);

    useEffect(() => {
        if (!exportingFacilityRoster) return;

        const abortController = new AbortController();
        const format = exportingFacilityRoster.format === 'csv' ? 'csv' : 'txt';
        networkOptimization
            .downloadFacilityRoster(
                networkOptimizationStore.runId!,
                networkOptimizationStore.revisionId!,
                exportingFacilityRoster.type,
                format,
                abortController.signal
            )
            .then(async (response) => {
                await streamResponseToDownload(response, `facility-roster-${exportingFacilityRoster.type}.zip`);
                setExportingFacilityRoster(null);
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    networkOptimizationStore.handleApiError(err, 'Download Failed');
                }
                setExportingFacilityRoster(null);
            });

        return () => abortController.abort();
    }, [exportingFacilityRoster, networkOptimizationStore]);

    useEffect(() => {
        if (!exportingCombinedRoster) return;

        const abortController = new AbortController();
        const format = exportingCombinedRoster.format === 'csv' ? 'csv' : 'txt';
        networkOptimization
            .downloadCombinedRoster(
                networkOptimizationStore.runId!,
                networkOptimizationStore.revisionId!,
                exportingCombinedRoster.type,
                format,
                abortController.signal
            )
            .then(async (response) => {
                await streamResponseToDownload(response, `combined-roster.zip`);
                setExportingCombinedRoster(null);
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    networkOptimizationStore.handleApiError(err, 'Download Failed');
                }
                setExportingCombinedRoster(null);
            });

        return () => abortController.abort();
    }, [exportingCombinedRoster, networkOptimizationStore]);

    const standardType = networkOptimizationStore.appliedGeographyFilter.standard_type;

    const rosterTabExportMenuItems: IExportProps['menuItems'] = useMemo(() => [
        createRosterMenuItem(
            'Provider Roster',
            exportingProviderRoster,
            setExportingProviderRoster,
            standardType
        ),
        createRosterMenuItem(
            'Facility Roster',
            exportingFacilityRoster,
            setExportingFacilityRoster,
            standardType
        ),
        createRosterMenuItem(
            'Combined Roster',
            exportingCombinedRoster,
            setExportingCombinedRoster,
            standardType
        )
    ], [exportingProviderRoster, exportingFacilityRoster, exportingCombinedRoster, standardType]);

    const reportPayload : IFetchReportsPayload = useMemo(() => ({
        roster_id: networkOptimizationStore.runId!,
        revision_id: networkOptimizationStore.revisionId,
        standard_type: networkOptimizationStore.geographyFilters.standard_type,
        standard_version: networkOptimizationStore.geographyFilters.standard_version,
        patient_roster_id: +networkOptimizationStore.geographyFilters.patient_roster_id,
        specialty_code: networkOptimizationStore.specialtyFilterCode ? networkOptimizationStore.specialtyFilterCode.split('|') : [],
        state: networkOptimizationStore.geographyFilters.state ? networkOptimizationStore.geographyFilters.state.split('|') : [],
        county: networkOptimizationStore.geographyFilters.county ? networkOptimizationStore.geographyFilters.county.split('|') : [],
        signal: new AbortController().signal
    }), [
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.geographyFilters.standard_type,
        networkOptimizationStore.geographyFilters.standard_version,
        networkOptimizationStore.geographyFilters.patient_roster_id,
        networkOptimizationStore.geographyFilters.state,
        networkOptimizationStore.geographyFilters.county,
        networkOptimizationStore.specialtyFilterCode
    ]);

    const disableExportReports = useMemo(() => {
        const no = networkOptimizationStore;
        const disabled = !([
            no.runId,
            no.revisionId >= 0 ? true : false,
            no.appliedGeographyFilter.standard_type,
            no.appliedGeographyFilter.standard_version,
            no.appliedGeographyFilter.patient_roster_name
        ].every(Boolean));
        return {
            providersReport: disabled || !!no.downloadingItems[DOWNLOAD_PROVIDERS_REPORT_ITEM],
            availableProvidersReport: disabled || !!no.downloadingItems[DOWNLOAD_AVAILABLE_PROVIDERS_REPORT_ITEM],
            adequacyScoresReport: disabled || !!no.downloadingItems[DOWNLOAD_ADEQUACY_SCORES_REPORT_ITEM],
            standardReporting: disabled || !!no.downloadingItems[DOWNLOAD_STANDARD_REPORTING_ITEM]
        };
    }, [
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.appliedGeographyFilter.standard_type,
        networkOptimizationStore.appliedGeographyFilter.standard_version,
        networkOptimizationStore.appliedGeographyFilter.patient_roster_name,
        networkOptimizationStore.appliedGeographyFilter.state,
        networkOptimizationStore.appliedGeographyFilter.county,
        networkOptimizationStore.specialtyFilter,
        selectedViewType,
        networkOptimizationStore.downloadingItems
    ]);

    const networkAdequacyTabExportMenuItems: IExportProps['menuItems'] = useMemo(() => [
        {
            disabled: disableExportReports.standardReporting,
            label: 'Standard Reporting',
            onClick: async () => {
                try {
                    await networkOptimizationStore.downloadReport(reportPayload, 'standard-reporting', DOWNLOAD_STANDARD_REPORTING_ITEM);
                } catch (err) {
                    networkOptimizationStore.handleApiError(err, 'Download Failed');
                }
            }
        },
        {
            disabled: disableExportReports.providersReport,
            label: 'Providers',
            onClick: async () => {
                try {
                    await networkOptimizationStore.downloadReport(reportPayload, 'providers', DOWNLOAD_PROVIDERS_REPORT_ITEM);
                } catch (err) {
                    networkOptimizationStore.handleApiError(err, 'Download Failed');
                }
            }
        },
        {
            disabled: disableExportReports.availableProvidersReport,
            label: 'Available Providers',
            onClick: async () => {
                try {
                    await networkOptimizationStore.downloadReport(reportPayload, 'available-providers', DOWNLOAD_AVAILABLE_PROVIDERS_REPORT_ITEM);
                } catch (err) {
                    networkOptimizationStore.handleApiError(err, 'Download Failed');
                }
            }
        },
        {
            disabled: disableExportReports.adequacyScoresReport,
            label: 'Adequacy Scores',
            onClick: async () => {
                try {
                    await networkOptimizationStore.downloadReport(reportPayload, 'adequacy-scores', DOWNLOAD_ADEQUACY_SCORES_REPORT_ITEM);
                } catch (err) {
                    networkOptimizationStore.handleApiError(err, 'Download Failed');
                }
            }
        }
    ], [disableExportReports, reportPayload, networkOptimizationStore]);

    const exportMenuItems = useMemo(
        () => activeTab === ReportsPageTab.Roster ? rosterTabExportMenuItems : networkAdequacyTabExportMenuItems,
        [activeTab, rosterTabExportMenuItems, networkAdequacyTabExportMenuItems]
    );

    const currentViewTypes = useMemo(() => {
        return activeTab === ReportsPageTab.Roster ? RosterTabViewTypes : NetworkAdequacyTabViewTypes;
    }, [activeTab]);

    useEffect(() => {
        setSelectedViewType(currentViewTypes[0]);
    }, [currentViewTypes]);

    const isInitialTab = useRef(true);
    useEffect(() => {
        if (isInitialTab.current) {
            isInitialTab.current = false;
            return;
        }
        networkOptimizationStore.resetGeographyFilter(['standard_type']);
    }, [activeTab]);

    const onActiveTabChange = (activeTabIndex: number) => {
        setActiveTab(activeTabIndex);
    };

    const onSelectedViewTypeChange = (viewType: ViewType) => {
        setSelectedViewType(viewType);
    };

    const generateReportPath = useMemo(() => {
        if (!rosterId || !revisionId) return '#';
        const _ = networkOptimizationStore;
        const searchParams = new URLSearchParams({
            standard_type: _.geographyFilters.standard_type,
            standard_version: _.geographyFilters.standard_version,
            patient_roster_id: _.geographyFilters.patient_roster_id,
            state: _.geographyFilters.state,
            // TODO: County and specialty filters are disabled for generate report for now, may need to re-enable in the future
            // county: _.geographyFilters.county,
            // specialty_filter_code: _.specialtyFilterCode
        });
        return `/network-optimization/${rosterId}/${revisionId}/generate-report?${searchParams.toString()}`;
    }, [
        rosterId,
        revisionId,
        networkOptimizationStore.geographyFilters.standard_type,
        networkOptimizationStore.geographyFilters.standard_version,
        networkOptimizationStore.geographyFilters.patient_roster_id,
        networkOptimizationStore.geographyFilters.state,
        // TODO: Re-enable when county and specialty filters are needed for generate report
        // networkOptimizationStore.geographyFilters.county,
        // networkOptimizationStore.specialtyFilterCode,
    ]);

    return {
        data: {
            tabs,
            rosterId,
            isExporting: Boolean(
                exportingProviderRoster || 
                exportingFacilityRoster || 
                exportingCombinedRoster ||
                Object.values(networkOptimizationStore.downloadingItems).some(Boolean)
            ),
            selectedViewType,
            networkOptimizationStore,
            activeTab,
            exportMenuItems,
            currentViewTypes,
            disableExportReports,
            generateReportPath
        },
        methods: {
            onSelectedViewTypeChange,
            onActiveTabChange,
        }
    };
};

export type IReportsPageController = ReturnType<typeof useReportsPageController>;

export default useReportsPageController;
